/**
 * KanbanCategorySelect - Dropdown compacto para escolher a categoria ativa
 * de um Kanban quando o usuário optou pelo formato "seleção" nas preferências.
 *
 * Substitui visualmente o título/tabs do Kanban por um Select com bolinha
 * colorida à esquerda de cada categoria, seguindo o padrão da imagem 1.
 *
 * Reutilizado por:
 *   - Kanban de Conversas (src/pages/tenant/Kanban.tsx)
 *   - Kanban de Tarefas (src/components/tasks/TaskKanbanBoard.tsx)
 *
 * @version 1.0.0
 * RELEASE: 2026-07-07 v1.0.0 - Criação inicial
 */

import type { LucideIcon } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

export interface KanbanCategoryOption {
  id: string;
  name: string;
  /** Cor hex (#RRGGBB) usada na bolinha à esquerda; opcional */
  color?: string | null;
  /** Total filtrado opcional, calculado no servidor pelo domínio. */
  count?: number;
}

interface KanbanCategorySelectProps {
  /** Ícone principal do Kanban exibido à esquerda do Select (ex.: KanbanIcon) */
  icon: LucideIcon;
  categories: KanbanCategoryOption[];
  selectedId: string | null;
  onChange: (id: string) => void;
  className?: string;
  /** Placeholder quando não há categoria selecionada */
  placeholder?: string;
}

export function KanbanCategorySelect({
  icon: Icon,
  categories,
  selectedId,
  onChange,
  className,
  placeholder = 'Selecione uma categoria',
}: KanbanCategorySelectProps) {
  const current = categories.find((c) => c.id === selectedId) ?? null;

  return (
    <div className={cn('flex items-center gap-2 min-w-0', className)}>
      <Icon className="h-5 w-5 text-primary shrink-0" />
      <Select value={selectedId ?? undefined} onValueChange={onChange}>
        <SelectTrigger className="h-9 min-w-[200px] max-w-[280px] font-semibold">
          <SelectValue placeholder={placeholder}>
            {current && (
              <span className="flex items-center gap-2 min-w-0">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: current.color || 'hsl(var(--muted-foreground))' }}
                />
                <span className="truncate">{current.name}</span>
                {current.count !== undefined && <span className="text-xs text-muted-foreground">{current.count}</span>}
              </span>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {categories.map((cat) => (
            <SelectItem key={cat.id} value={cat.id}>
              <span className="flex items-center gap-2">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: cat.color || 'hsl(var(--muted-foreground))' }}
                />
                <span>{cat.name}</span>
                {cat.count !== undefined && <span className="text-xs text-muted-foreground">{cat.count}</span>}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
